import { defineComponent as _defineComponent } from 'vue'

export enum OzConfirmationDialogBoxButtonScheme {
  // `confirmButton` is highlighted as the primary action
  Default = 'Default',
  // `confirmButton` is highlighted in red to signal danger
  Danger = 'Danger',
  // `cancelButton` is positioned as the primary action. `confirmButton`'s color is muted
  PromoteCancellation = 'PromoteCancellation',
  // Features a discard button, along with save and cancel
  SaveDiscardCancel = 'SaveDiscardCancel',
  // Features a discard and a cancel button
  DiscardCancel = 'DiscardCancel',
}

const __default__ = {}

import { __ } from '@@/bits/intl'
import isChromatic from 'chromatic/isChromatic'
import type { OzBaseDialogBoxButtonData } from '@@/library/v4/components/OzBaseDialogBox.vue'
import OzBaseDialogBox, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzBaseDialogBox.vue'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzInput from '@@/library/v4/components/OzInput.vue'
import { computed, nextTick, ref, watch } from 'vue'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'OzConfirmationDialogBox',
  props: {
    title: null,
    darkMode: { type: [Boolean, String], default: 'auto' },
    iconSrc: { default: null },
    iconAlt: { default: null },
    body: { default: undefined },
    customBodyClasses: { default: undefined },
    xShadow: { type: Boolean, default: false },
    isCodeProtected: { type: Boolean, default: false },
    confirmButtonText: { default: undefined },
    cancelButtonText: { default: undefined },
    discardButtonText: { default: undefined },
    buttonScheme: { default: OzConfirmationDialogBoxButtonScheme.Default },
    forceFullWidthButtons: { type: Boolean, default: false }
  },
  emits: ['cancel', 'confirm', 'discard'],
  setup(__props: any, { expose, emit }) {

const props = __props as { title: string, darkMode: boolean | 'auto', iconSrc: string | null, iconAlt: string | null, body: string, customBodyClasses: string, xShadow: boolean, isCodeProtected: boolean, confirmButtonText: string, cancelButtonText: string, discardButtonText: string, buttonScheme: OzConfirmationDialogBoxButtonScheme, forceFullWidthButtons: boolean }





// Fix this for Chromatic comparison
const generateCode = (): string =>
  isChromatic()
    ? '0539'
    : Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, '0')

const confirmationCodeInput = ref<InstanceType<typeof OzInput>>()
const codeConfirmationText = ref<string>(__('Enter this code to proceed:'))
const confirmationCode = ref<string>(generateCode())
const enteredCode = ref<string>('')
const isSubmittedCodeValid = ref<boolean>(true)

watch(enteredCode, () => {
  if (enteredCode.value.length > 0) {
    isSubmittedCodeValid.value = true
  }
})

const buttons = computed((): OzBaseDialogBoxButtonData[] => {
  const cancelButton = {
    text: props.cancelButtonText || __('Cancel'),
    emit: 'cancel',
    colorScheme: OzContainedButtonColorScheme.Secondary,
    testId: 'cancelButton',
  }

  const confirmButton = {
    text: props.confirmButtonText || __('Continue'),
    emit: 'confirm',
    colorScheme: OzContainedButtonColorScheme.Primary,
    disabled: props.isCodeProtected && !enteredCode.value.trim(),
    testId: 'confirmButton',
    isFocusedOnLoad: true,
  }

  const discardButton = {
    text: props.discardButtonText || __('Discard changes'),
    emit: 'discard',
    colorScheme: OzContainedButtonColorScheme.SecondaryDestructive,
    testId: 'discardButton',
  }

  // When the buttons are in a row, the prioritized action is right-most.
  // When the buttons are stacked, the prioritized action is top-most.
  let buttonList: OzBaseDialogBoxButtonData[]

  if (props.buttonScheme === OzConfirmationDialogBoxButtonScheme.Default) {
    buttonList = [cancelButton, confirmButton]
  } else if (props.buttonScheme === OzConfirmationDialogBoxButtonScheme.Danger) {
    buttonList = [cancelButton, { ...confirmButton, colorScheme: OzContainedButtonColorScheme.Destructive }]
  } else if (props.buttonScheme === OzConfirmationDialogBoxButtonScheme.PromoteCancellation) {
    buttonList = [{ ...confirmButton, colorScheme: OzContainedButtonColorScheme.SecondaryClear }, cancelButton]
  } else if (props.buttonScheme === OzConfirmationDialogBoxButtonScheme.SaveDiscardCancel) {
    buttonList = [
      { ...cancelButton, colorScheme: OzContainedButtonColorScheme.SecondaryClear },
      discardButton,
      confirmButton,
    ]
  } else if (props.buttonScheme === OzConfirmationDialogBoxButtonScheme.DiscardCancel) {
    buttonList = [{ ...cancelButton, colorScheme: OzContainedButtonColorScheme.SecondaryClear }, discardButton]
  } else {
    buttonList = [cancelButton, confirmButton]
  }

  return props.forceFullWidthButtons ? buttonList.reverse() : buttonList
})

const focus = (): void => {
  nextTick(() => confirmationCodeInput.value?.focusInput())
}

const cancel = (): void => {
  emit('cancel')
}

const confirm = (): void => {
  emit('confirm')
}

const discard = (): void => {
  emit('discard')
}

const submit = (): void => {
  if (!props.isCodeProtected) {
    confirm()
  } else if (enteredCode.value.trim() === confirmationCode.value) {
    confirm()
  } else {
    isSubmittedCodeValid.value = false
    focus()
    confirmationCode.value = generateCode()
  }
  enteredCode.value = ''
}

expose({
  focus,
})

return { __sfc: true,OzConfirmationDialogBoxButtonScheme, props, emit, generateCode, confirmationCodeInput, codeConfirmationText, confirmationCode, enteredCode, isSubmittedCodeValid, buttons, focus, cancel, confirm, discard, submit, __, OzBaseDialogBox, OzBox, OzBoxColors, OzInput }
}

})